import { validateMessage } from '../utils/chat/contentModeration';
import { getToken, getUserTypeFromToken } from '../utils/auth/tokenManager';
import { fetchInfluencerInvites } from '../hooks/collaborations/useInfluencerCollaborations';

const API_BASE = process.env.REACT_APP_API_URL;
const DEFAULT_MESSAGE_LIMIT = 50;

// Internal API call counters (used for debugging/instrumentation). Only exposed
// on window in development builds to avoid leaking internals in production.
const apiCallCounts = {
  fetchConversations: 0,
  fetchMessages: 0,
  sendMessage: 0,
  total: 0,
};

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.chatApiCallCounts = apiCallCounts;
}

function authError(message) {
  const error = new Error(message);
  error.status = 401;
  return error;
}

function authHeaders() {
  const token = getToken();
  if (!token) {
    throw authError('Missing authentication token');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/** Reject obviously malformed conversation ids before they reach the server path. */
function encodeConversationId(conversationId) {
  if (conversationId == null || conversationId === '') {
    const error = new Error('Invalid conversation id');
    error.status = 400;
    throw error;
  }
  return encodeURIComponent(conversationId);
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);

  if (options.signal?.aborted) {
    const error = new Error('Request aborted');
    error.name = 'AbortError';
    throw error;
  }

  if (!res.ok) {
    const error = new Error(`Request failed ${res.status}`);
    error.status = res.status;
    try {
      error.data = await res.json();
    } catch (e) {
      error.data = null;
    }
    throw error;
  }
  return res.json();
}

/**
 * Fetch two conversation groups in parallel. Transient/network failures for a
 * single group degrade to an empty list, but authentication failures (401/403)
 * are propagated so callers can react instead of silently showing an empty UI.
 */
async function fetchTypedConversations(invitesUrl, applicationsUrl) {
  const swallowNonAuth = (err) => {
    if (err && (err.status === 401 || err.status === 403)) {
      throw err;
    }
    return [];
  };

  const [invites, applications] = await Promise.all([
    fetchJson(invitesUrl, { headers: authHeaders() }).catch(swallowNonAuth),
    fetchJson(applicationsUrl, { headers: authHeaders() }).catch(swallowNonAuth),
  ]);

  const invitesWithType = (invites || []).map((conv) => ({ ...conv, conversation_type: 'invite' }));
  const applicationsWithType = (applications || []).map((conv) => ({ ...conv, conversation_type: 'application' }));

  return [...invitesWithType, ...applicationsWithType];
}

const ChatService = {
  async fetchConversations(userType) {
    if (userType !== 'brand' && userType !== 'influencer') {
      return [];
    }

    const tokenType = getUserTypeFromToken(getToken());
    if (tokenType && tokenType !== userType) {
      return [];
    }

    apiCallCounts.fetchConversations += 1;
    apiCallCounts.total += 1;

    if (userType === 'brand') {
      return fetchTypedConversations(
        `${API_BASE}/api/chat_messages/brand/negotiating`,
        `${API_BASE}/api/application_chat_messages/brand/approved`,
      );
    }

    // Influencer invites share the collaborations module cache (TTL + single-flight).
    const swallowNonAuth = (err) => {
      if (err && (err.status === 401 || err.status === 403)) {
        throw err;
      }
      return [];
    };

    const [invites, applications] = await Promise.all([
      fetchInfluencerInvites(false).catch(swallowNonAuth),
      fetchJson(
        `${API_BASE}/api/application_chat_messages/influencer/approved`,
        { headers: authHeaders() },
      ).catch(swallowNonAuth),
    ]);

    const invitesWithType = (invites || []).map((conv) => ({ ...conv, conversation_type: 'invite' }));
    const applicationsWithType = (applications || []).map((conv) => ({
      ...conv,
      conversation_type: 'application',
    }));

    return [...invitesWithType, ...applicationsWithType];
  },

  async fetchMessages(conversationId, conversationType = 'invite', signal = null, options = {}) {
    apiCallCounts.fetchMessages += 1;
    apiCallCounts.total += 1;

    const safeId = encodeConversationId(conversationId);
    const query = new URLSearchParams();
    query.set('limit', String(options.limit || DEFAULT_MESSAGE_LIMIT));
    if (options.before) {
      query.set('before', options.before);
    }

    const baseUrl = conversationType === 'application'
      ? `${API_BASE}/api/application_chat_messages/${safeId}`
      : `${API_BASE}/api/chat_messages/${safeId}`;
    const url = `${baseUrl}?${query.toString()}`;

    return fetchJson(url, { headers: authHeaders(), signal });
  },

  async sendMessage({ conversationId, conversationType = 'invite', message }) {
    apiCallCounts.sendMessage += 1;
    apiCallCounts.total += 1;

    // Validate message content before sending (safety net)
    const validation = validateMessage(message);
    if (!validation.isValid) {
      const error = new Error(validation.error || 'Message validation failed');
      error.status = 400;
      error.data = {
        error: 'Content Violation',
        message: validation.error,
      };
      throw error;
    }

    encodeConversationId(conversationId);

    const url = conversationType === 'application'
      ? `${API_BASE}/api/application_chat_messages`
      : `${API_BASE}/api/chat_messages`;

    const bodyKey = conversationType === 'application'
      ? 'campaign_application_id'
      : 'campaign_influencer_id';

    return fetchJson(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        [bodyKey]: conversationId,
        message,
      }),
    });
  },

  getCallCounts() {
    return { ...apiCallCounts };
  },

  resetCounters() {
    apiCallCounts.fetchConversations = 0;
    apiCallCounts.fetchMessages = 0;
    apiCallCounts.sendMessage = 0;
    apiCallCounts.total = 0;
  },
};

export default ChatService;
